import React, { useEffect, useRef, useState } from "react";
import { useI18n } from "@/lib/i18n";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Bot, Send, User } from "lucide-react";
import AiActionCard from "@/components/AiActionCard";

const SUGGESTIONS_FR = [
  "Quels sont mes bassins les plus rentables ?",
  "Quelles récoltes arrivent dans les 30 prochains jours ?",
  "Quels stocks risquent la rupture ?",
  "Quelle quantité d'aliment prévoir ce mois-ci ?",
];
const SUGGESTIONS_EN = [
  "Which ponds are the most profitable?",
  "Which harvests are due in the next 30 days?",
  "Which stocks are at risk of running out?",
  "How much feed should I plan this month?",
];

const MODEL_OPTIONS = [
  { value: "gpt-5.4", label: "GPT-5.4" },
  { value: "gpt-5.4-mini", label: "GPT-5.4 Mini" },
  { value: "gpt-5.5", label: "GPT-5.5" },
  { value: "gpt-5.6-sol", label: "GPT-5.6 Sol" },
  { value: "claude-sonnet-5", label: "Claude Sonnet 5" },
  { value: "claude-opus-4-8", label: "Claude Opus 4.8" },
  { value: "gemini-3.1-pro", label: "Gemini 3.1 Pro" },
  { value: "gemini-3.5-flash", label: "Gemini 3.5 Flash" },
];

export default function Assistant() {
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [model, setModel] = useState("gpt-5.4");
  const bottomRef = useRef(null);
  const SUGGESTIONS = lang === "en" ? SUGGESTIONS_EN : SUGGESTIONS_FR;

  useEffect(() => {
    const API = process.env.REACT_APP_BACKEND_URL;
    const token = localStorage.getItem("agrinova_token");
    fetch(`${API}/api/ai/history`, { headers: { Authorization: `Bearer ${token}` }, credentials: "include" })
      .then((r) => (r.ok ? r.json() : []))
      .then((msgs) => {
        setMessages(msgs.map((m) => ({ role: m.role, content: m.content, model: m.model })));
        if (user?.role === "invité") return;
        const today = new Date().toISOString().slice(0, 10);
        // Résumé matinal — une fois par jour, TOUT EN HAUT (inséré après les alertes)
        const loadBrief = () => {
          if (localStorage.getItem("ai_brief_date") !== today) {
            fetch(`${API}/api/ai/morning-brief?lang=${lang}`, { headers: { Authorization: `Bearer ${token}` } })
              .then((r) => r.json())
              .then((b) => {
                if (b?.message) {
                  localStorage.setItem("ai_brief_date", today);
                  setMessages((m) => [{ role: "assistant", content: b.message }, ...m]);
                }
              })
              .catch(() => {});
          }
        };
        // Récap hebdomadaire — le lundi uniquement, sous le résumé matinal
        const loadRecap = () => {
          const now = new Date();
          const week = `${now.getFullYear()}-W${Math.ceil((now - new Date(now.getFullYear(), 0, 1)) / 604800000)}`;
          if (now.getDay() === 1 && localStorage.getItem("ai_recap_week") !== week) {
            fetch(`${API}/api/ai/weekly-recap?lang=${lang}`, { headers: { Authorization: `Bearer ${token}` } })
              .then((r) => r.json())
              .then((b) => {
                if (b?.message) {
                  localStorage.setItem("ai_recap_week", week);
                  setMessages((m) => [{ role: "assistant", content: b.message }, ...m]);
                }
              })
              .catch(() => {})
              .finally(loadBrief);
          } else {
            loadBrief();
          }
        };
        // Alertes proactives (membres) — une fois par 6 h, sous le résumé
        const last = Number(sessionStorage.getItem("ai_proactive_at") || 0);
        if (Date.now() - last > 6 * 3600 * 1000) {
          fetch(`${API}/api/ai/proactive`, { headers: { Authorization: `Bearer ${token}` } })
            .then((r) => r.json())
            .then((alerts) => {
              if (Array.isArray(alerts) && alerts.length) {
                sessionStorage.setItem("ai_proactive_at", String(Date.now()));
                setMessages((m) => [...alerts.map((a) => ({ role: "assistant", content: a.message, action: a.action })), ...m]);
              }
            })
            .catch(() => {})
            .finally(loadRecap);
        } else {
          loadRecap();
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  async function ask(question) {
    const q = (question || input).trim();
    if (!q || streaming) return;
    setInput("");
    setStreaming(true);
    setMessages((m) => [...m, { role: "user", content: q }, { role: "assistant", content: "", model }]);
    try {
      const API = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem("agrinova_token");
      const resp = await fetch(`${API}/api/ai/assistant`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        credentials: "include",
        body: JSON.stringify({ question: q, lang, model }),
      });
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          if (!part.startsWith("data: ")) continue;
          const payload = part.slice(6);
          if (payload === "[DONE]") continue;
          try {
            const ev = JSON.parse(payload);
            if (ev.action) {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { ...copy[copy.length - 1], action: ev.action };
                return copy;
              });
            }
            if (ev.delta) {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { ...copy[copy.length - 1], content: copy[copy.length - 1].content + ev.delta };
                return copy;
              });
            }
          } catch { /* partial json */ }
        }
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { ...copy[copy.length - 1], content: `[${t("error")}]` };
        return copy;
      });
    }
    setStreaming(false);
  }

  return (
    <div className="max-w-3xl mx-auto space-y-6" data-testid="assistant-page">
      <div>
        <div className="label-eyebrow mb-2">AgriNova Intelligence</div>
        <h1 className="text-3xl md:text-4xl font-bold tracking-tight flex items-center gap-3">
          <Bot className="w-8 h-8 text-primary" />{t("ai_assistant")}
        </h1>
        <p className="text-muted-foreground mt-1">{t("ai_sub")}</p>
      </div>

      <Card className="border-border">
        <CardContent className="p-0">
          <div className="h-[480px] overflow-y-auto p-5 space-y-4" data-testid="chat-messages">
            {messages.length === 0 && (
              <div className="h-full flex flex-col items-center justify-center text-center gap-4">
                <div className="w-14 h-14 rounded-2xl bg-primary/10 text-primary flex items-center justify-center">
                  <Bot className="w-7 h-7" />
                </div>
                <div className="text-sm text-muted-foreground max-w-sm">{t("ai_welcome")}</div>
                <div className="flex flex-wrap gap-2 justify-center">
                  {SUGGESTIONS.map((s) => (
                    <button key={s} onClick={() => ask(s)}
                      className="text-xs border border-border rounded-full px-3 py-1.5 hover:border-primary/50 hover:text-primary transition-colors"
                      data-testid={`suggestion-${SUGGESTIONS.indexOf(s)}`}>
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={`flex gap-3 ${m.role === "user" ? "justify-end" : ""}`}>
                {m.role === "assistant" && (
                  <div className="w-8 h-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
                    <Bot className="w-4 h-4" />
                  </div>
                )}
                <div className="max-w-[80%] space-y-1.5">
                  <div className={`rounded-xl px-4 py-2.5 text-sm whitespace-pre-wrap ${
                    m.role === "user" ? "bg-primary text-primary-foreground" : "bg-secondary"
                  }`} data-testid={`msg-${i}`}>
                    {(m.content || "").split("ACTION_JSON:")[0] || (streaming && i === messages.length - 1 ? "…" : "")}
                    {m.role === "assistant" && m.model && (
                      <div className="mt-1 text-[10px] uppercase tracking-wider text-muted-foreground" data-testid={`msg-model-${i}`}>
                        {MODEL_OPTIONS.find((o) => o.value === m.model)?.label || m.model}
                      </div>
                    )}
                  </div>
                  {m.action && <AiActionCard action={m.action} />}
                </div>
                {m.role === "user" && (
                  <div className="w-8 h-8 rounded-lg bg-accent/20 text-accent-foreground flex items-center justify-center shrink-0">
                    <User className="w-4 h-4" />
                  </div>
                )}
              </div>
            ))}
            <div ref={bottomRef} />
          </div>
          <form
            onSubmit={(e) => { e.preventDefault(); ask(); }}
            className="border-t border-border p-4 flex gap-2"
          >
            <Select value={model} onValueChange={setModel} disabled={streaming}>
              <SelectTrigger className="w-[170px] shrink-0" data-testid="ai-model-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {MODEL_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value} data-testid={`ai-model-${o.value}`}>{o.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={t("ai_placeholder")}
              disabled={streaming}
              data-testid="ai-input"
            />
            <Button type="submit" disabled={streaming || !input.trim()} data-testid="ai-send">
              <Send className="w-4 h-4" />
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
